import React, { useState, useEffect } from "react";
import VendorHeader from "../components/vendorManagement/VendorHeader";
import VendorTable from "../components/vendorManagement/VendorTable";
import AddVendorModal from "../components/vendorManagement/AddVendorModal";
import {
  useCreateVendor,
  useUpdateVendor,
  useDeleteVendor,
  Vendor,
} from "../hooks/useVendors";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate } from "react-router-dom";
import { Alert } from "antd";
import { useAuth } from "../hooks/useAuth";

const VendorManagementPage: React.FC = () => {
  const { data: user } = useAuth();
  const locationId = user?.location?.id;
  const navigate = useNavigate();

  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);

  // Get user permissions
  const { hasPermission } = usePermissions();
  const canViewVendors = hasPermission(PERMISSIONS.VIEW_VENDORS);
  const canAddVendors = hasPermission(PERMISSIONS.ADD_VENDORS);
  const canModifyVendors = hasPermission(PERMISSIONS.MODIFY_VENDORS);
  const canDeleteVendors = hasPermission(PERMISSIONS.DELETE_VENDORS);
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Initialize filters - only include location_id if user can't see all locations
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      initialFilters.location_id = locationId;
    }
    return initialFilters;
  });

  const { mutate: createVendor } = useCreateVendor();
  const { mutate: updateVendor } = useUpdateVendor();
  const { mutate: deleteVendor } = useDeleteVendor();

  // Redirect if user doesn't have permission to view vendors
  useEffect(() => {
    if (!canViewVendors) {
      // Redirect to unauthorized page with a delay to prevent render issues
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [canViewVendors, navigate]);

  // Permission-aware handlers
  const handleAddVendor = () => {
    if (!canAddVendors) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedVendor(null);
    setModalVisible(true);
  };

  const handleModifyVendor = (vendor: Vendor) => {
    if (!canModifyVendors) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedVendor(vendor);
    setModalVisible(true);
  };

  const handleDeleteVendor = (vendorId: number) => {
    if (!canDeleteVendors) {
      return; // Don't delete if user doesn't have permission
    }
    deleteVendor(vendorId);
  };

  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Ensure location filter is maintained for users who can't see all locations
    if (!canSeeAllLocations && locationId) {
      newFilters.location_id = locationId;
    }
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      clearedFilters.location_id = locationId;
    }
    setFilters(clearedFilters);
  };

  const handleSubmit = (values: any) => {
    if (selectedVendor) {
      if (!canModifyVendors) return;
      updateVendor({ ...values, id: selectedVendor.id });
    } else {
      if (!canAddVendors) return;
      createVendor(values);
    }
  };

  // If user doesn't have permission to view vendors, show access denied message
  if (!canViewVendors) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access vendor management. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <VendorHeader
        onAddVendor={handleAddVendor}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        canAddVendors={canAddVendors}
        canSeeAllLocations={canSeeAllLocations}
      />
      <VendorTable
        filters={filters}
        onModifyVendor={handleModifyVendor}
        onDeleteVendor={handleDeleteVendor}
        canModifyVendors={canModifyVendors}
        canDeleteVendors={canDeleteVendors}
      />

      <AddVendorModal
        visible={isModalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleSubmit}
        vendor={selectedVendor}
        canSeeAllLocations={canSeeAllLocations}
        userLocationId={locationId}
      />
    </div>
  );
};

export default VendorManagementPage;
